import React, { useEffect, useRef, useState } from "react";
import { Maximize2, Pencil, Trash2, X, ChevronLeft, ChevronRight } from "lucide-react";

const PhotoMenu = ({ f, src, canEdit, onZoom, onEdit, onDelete, onClose }) => (
  <div className="fixed inset-0 !mt-0 z-[70] flex items-end md:items-center justify-center p-3" style={{ background: "rgba(0,0,0,.5)" }} onClick={onClose} data-testid="photo-menu">
    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <img src={src} alt={f.original_filename} className="w-full h-40 object-cover" />
      <div className="px-4 pt-2 text-xs truncate" style={{ color: "var(--text-muted)" }}>{f.original_filename}</div>
      <div className="p-3 grid gap-2">
        <button onClick={onZoom} className="btn-primary justify-center" data-testid="photo-menu-zoom"><Maximize2 className="w-4 h-4" /> Ingrandisci</button>
        {canEdit && <button onClick={onEdit} className="btn-secondary justify-center" data-testid="photo-menu-edit"><Pencil className="w-4 h-4" /> Modifica</button>}
        {canEdit && <button onClick={onDelete} className="btn-secondary justify-center" style={{ color: "#B30000" }} data-testid="photo-menu-delete"><Trash2 className="w-4 h-4" /> Cancella</button>}
        <button onClick={onClose} className="text-sm py-2" style={{ color: "var(--text-muted)" }} data-testid="photo-menu-cancel">Annulla</button>
      </div>
    </div>
  </div>
);

const PhotoViewer = ({ items, idx, setIdx, onClose }) => {
  const touch = useRef(null);
  const n = items.length;
  const go = (d) => setIdx((i) => (i + d + n) % n);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const it = items[idx];
  return (
    <div className="fixed inset-0 !mt-0 z-[75] bg-black flex items-center justify-center" data-testid="photo-viewer"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const d = e.changedTouches[0].clientX - (touch.current ?? 0); if (n > 1 && Math.abs(d) > 50) go(d < 0 ? 1 : -1); touch.current = null; }}>
      <img src={it.src} alt={it.f.original_filename} className="max-w-full max-h-full object-contain select-none" draggable={false} data-testid="photo-viewer-img" />
      <button onClick={onClose} className="absolute top-3 right-3 p-2.5 rounded-full bg-white/15 text-white" style={{ marginTop: "env(safe-area-inset-top)" }} data-testid="photo-viewer-close"><X className="w-6 h-6" /></button>
      <div className="absolute top-4 left-4 text-white/80 text-xs max-w-[70%] truncate" style={{ marginTop: "env(safe-area-inset-top)" }} data-testid="photo-viewer-counter">{idx + 1} / {n} · {it.f.original_filename}</div>
      {n > 1 && <>
        <button onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/15 text-white" data-testid="photo-viewer-prev"><ChevronLeft className="w-6 h-6" /></button>
        <button onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/15 text-white" data-testid="photo-viewer-next"><ChevronRight className="w-6 h-6" /></button>
      </>}
    </div>
  );
};

// items: [{ f, src }] immagini della sezione; ritorna open(fileId) + ui da renderizzare
export const usePhotoActions = ({ items, canEdit, onEdit, onDelete }) => {
  const [menu, setMenu] = useState(null);
  const [view, setView] = useState(null);
  useEffect(() => {
    if (view === null) return undefined;
    window.history.pushState({ photoViewer: true }, "");
    const pop = () => setView(null);
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [view === null]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeView = () => { if (window.history.state?.photoViewer) window.history.back(); else setView(null); };
  const cur = items.find((x) => x.f.id === menu);
  const ui = (
    <>
      {cur && <PhotoMenu f={cur.f} src={cur.src} canEdit={canEdit} onClose={() => setMenu(null)}
        onZoom={() => { setView(items.indexOf(cur)); setMenu(null); }}
        onEdit={() => { setMenu(null); onEdit(cur.f); }}
        onDelete={() => { setMenu(null); onDelete(cur.f); }} />}
      {view !== null && items[view] && <PhotoViewer items={items} idx={view} setIdx={setView} onClose={closeView} />}
    </>
  );
  return { open: setMenu, ui };
};
